import { useEffect, useState } from "react";

import { Button, Col, Row, Table } from "reactstrap";

// router
import { useLocation } from "react-router-dom";

// componentes
import Pagination from "../../../components/Paginationv3";
import LoadingSkelentonTable from "../../../components/loadingSkeletonTable";
import PlayersExtract from "../ranking/modalPlayersExtract";
import DeleteButton from "../../../components/Buttons/DeleteButton";
import SweetAlert from "react-bootstrap-sweetalert";
import WaitExport from "../../../components/WaitExport";

// services
import {
  getConfigurations,
  getRankingByConfiguration,
  deleteRankingByConfiguration,
} from "../../../utils/services/ranking";

// utils
import useDebounce from "../../../utils/hooks/useDebounce";
import {
  columnsForConfig,
  isMatchConfig,
  formatMatch,
  labelForConfig,
  gameConfigs,
  TOTAL_RANKING_CODE,
  TOTAL_RANKING_OPTION,
} from "./rankingConfig";
import { createXlsx } from "./createXlsx";

import { format } from "date-fns";

// selectConfigs decide quais configurações do evento entram no filtro deste
// card: o mesmo componente serve ao Ranking Games e ao Ranking Interação.
//
// totalLabel nomeia a opção consolidada do filtro e, por tabela, a planilha
// exportada dela — cada card chama o seu total pelo próprio nome, já que
// "total por categoria" não diz qual categoria é
export default function RankingList({
  selectConfigs = gameConfigs,
  totalLabel = TOTAL_RANKING_OPTION.description,
}) {
  const location = useLocation();
  const idEvent = location?.state?.idEvent;

  // filtros — nasce vazio e assume a primeira configuração assim que a lista
  // chega, já que todas as opções dependem da api
  const [rankingCode, setRankingCode] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  // opções do filtro: só as configurações de games que o evento tem
  // cadastradas que o selectConfigs deste card aceita
  const [rankingConfigs, setRankingConfigs] = useState([]);

  // códigos que a opção consolidada soma: os da categoria deste card
  const [categoryCodes, setCategoryCodes] = useState([]);

  // o que a api recebe. No total vai a lista de códigos da categoria, porque
  // TOTAL_RANKING_CODE é sentinela do front e não existe no banco; nas demais
  // opções, o próprio código
  const apiCode =
    rankingCode === TOTAL_RANKING_CODE ? categoryCodes.join(",") : rankingCode;

  // movimentos, tempo e a exclusão da pontuação só existem nas configurações
  // de partida (MALA, PASSARELA) — no total, que soma vários códigos, não há
  // uma partida única a mostrar nem um código único a excluir
  const columns = columnsForConfig(rankingCode);
  const showDelete = isMatchConfig(rankingCode);

  // paginação
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [countData, setCountData] = useState(0);

  // dados
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // extrato de pontos do participante
  const [selectedPlayer, setSelectedPlayer] = useState({});
  const [extractPointsModal, setExtractPointsModal] = useState(false);

  // exclusão da pontuação
  const [deleteConfirmAlert, setDeleteConfirmAlert] = useState(false);
  const [deleteSuccessAlert, setDeleteSuccessAlert] = useState(false);
  const [deleteErrorAlert, setDeleteErrorAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // exportação
  const [exportInProgress, setExportInProgress] = useState(false);
  const [finishExport, setFinishExport] = useState(false);
  const [exportErrorAlert, setExportErrorAlert] = useState(false);

  // busca a página atual. O Ranking Geral vem do mesmo endpoint de
  // /event/ranking; as configurações por jogo ainda não têm endpoint
  async function getListData() {
    setIsLoading(true);

    try {
      const params = {
        offset: currentIndex > 0 ? currentIndex : 1,
        limit: itemsPerPage,
        search: debouncedSearch,
      };

      const res = await getRankingByConfiguration({
        eventId: idEvent,
        code: apiCode,
        columnSearch: "name",
        ...params,
      });

      setDataList(res?.data ?? []);
      setCountData(res?.count ?? 0);
    } catch (error) {
      setDataList([]);
      setCountData(0);
    } finally {
      setIsLoading(false);
    }
  }

  // carrega os códigos das configurações de pontuação do evento. limit alto de
  // propósito: o endpoint é paginado e o select precisa de todos de uma vez
  async function getRankingConfigs() {
    try {
      const res = await getConfigurations({
        id: idEvent,
        offset: 1,
        limit: 500,
      });

      const cadastrados = (res?.data ?? []).map((config) =>
        String(config.code).toUpperCase(),
      );

      const codes = selectConfigs(cadastrados);

      const configs = codes.map((code) => ({
        code,
        // rótulo legível no lugar do código cru (POSTPHOTOCREATE001 → "Post
        // com Foto"); o value do option continua sendo o código
        description: labelForConfig(code),
      }));

      setCategoryCodes(codes);

      // o total encabeça a lista, mas só quando há configuração para somar:
      // sem nenhuma, seria um total de nada
      const options =
        codes.length > 0
          ? [{ ...TOTAL_RANKING_OPTION, description: totalLabel }, ...configs]
          : [];

      setRankingConfigs(options);
      // seleciona a primeira assim que a lista chega; sem isso a tela fica
      // sem nada selecionado e sem lista
      if (options.length > 0) setRankingCode(options[0].code);
    } catch (error) {
      // sem as configurações o filtro fica vazio e a lista não é buscada
    }
  }

  useEffect(() => {
    getRankingConfigs();
  }, [idEvent]);

  useEffect(() => {
    // apiCode, e não rankingCode: no total ele só fica pronto quando os
    // códigos da categoria chegam, e sem code o endpoint não tem o que filtrar
    if (!apiCode) return;

    getListData();
  }, [apiCode, debouncedSearch, currentIndex, itemsPerPage]);

  // exporta a listagem com os filtros da tela aplicados
  async function exportRanking() {
    setExportInProgress(true);

    try {
      await createXlsx({
        eventId: idEvent,
        rankingCode,
        apiCode,
        totalLabel,
        search: debouncedSearch,
        setFinishExport,
      });
    } catch (error) {
      // fecha a barra de progresso antes do alerta: deixá-la aberta daria a
      // impressão de que a exportação continua
      setExportInProgress(false);
      setExportErrorAlert(true);
    }
  }

  // exclusão da pontuação do participante na configuração selecionada: abate
  // do total do ranking geral e remove a partida registrada
  async function deleteScore() {
    setIsDeleting(true);

    try {
      const res = await deleteRankingByConfiguration({
        eventId: idEvent,
        loginId: selectedPlayer?.loginId,
        code: rankingCode,
      });

      setDeleteConfirmAlert(false);

      // a api devolve { deletedCount }; qualquer outra coisa é erro tratado
      // pelo service, que retorna o corpo da resposta em vez de lançar
      if (res?.deletedCount > 0) {
        setDeleteSuccessAlert(true);
      } else {
        setDeleteErrorAlert(true);
      }
    } catch (error) {
      setDeleteConfirmAlert(false);
      setDeleteErrorAlert(true);
    } finally {
      setIsDeleting(false);
    }
  }

  // guarda o participante para os modais. o eventId entra aqui porque o
  // endpoint por configuração não devolve esse campo (o do Ranking Geral
  // devolve), e o extrato de pontos o usa para buscar a listagem
  function selectPlayer(row) {
    setSelectedPlayer({ ...row, eventId: row?.eventId ?? idEvent });
  }

  // conteúdo de uma célula; percorrer as colunas em vez de fixar os <td>
  // mantém corpo e cabeçalho alinhados quando o Ranking Geral esconde as duas
  // colunas de partida
  function renderCell(row, key) {
    if (key === "match") return formatMatch(row);

    if (key === "updatedAt") {
      return row.updatedAt
        ? format(new Date(row.updatedAt), "dd/MM/yyyy HH:mm:ss")
        : "";
    }

    if (key === "actions") {
      return (
        <>
          <i
            className="mdi mdi-eye"
            title="Ver extrato de pontos"
            style={{
              color: "blue",
              fontSize: 17,
              cursor: "pointer",
              marginRight: 5,
            }}
            onClick={() => {
              selectPlayer(row);
              setExtractPointsModal(true);
            }}
          />
          {showDelete && (
            <DeleteButton
              onClick={() => {
                selectPlayer(row);
                setDeleteConfirmAlert(true);
              }}
              color="red"
              fontSize={17}
            />
          )}
        </>
      );
    }

    return row[key];
  }

  return (
    <>
      <PlayersExtract
        isOpen={extractPointsModal}
        toggle={() => setExtractPointsModal(!extractPointsModal)}
        playerData={selectedPlayer}
        getAllRankingFunc={getListData}
        hideDelete
        // o extrato mostra só as pontuações da configuração selecionada — no
        // total, as de todos os códigos da categoria, para bater com a soma
        configurationCode={apiCode}
      />

      {deleteConfirmAlert && (
        <SweetAlert
          showCancel
          warning
          title="Atenção"
          confirmBtnText={isDeleting ? "Excluindo..." : "Sim"}
          cancelBtnText="Não"
          confirmBtnBsStyle="success"
          cancelBtnBsStyle="danger"
          disabled={isDeleting}
          onConfirm={() => deleteScore()}
          onCancel={() => setDeleteConfirmAlert(false)}
        >
          {`Deseja excluir a pontuação de ${selectedPlayer?.name} em ${labelForConfig(rankingCode)}? Os pontos serão descontados do Ranking Geral.`}
        </SweetAlert>
      )}

      {deleteSuccessAlert && (
        <SweetAlert
          success
          title="Sucesso"
          onConfirm={() => {
            setDeleteSuccessAlert(false);
            getListData();
          }}
        >
          Pontuação excluída com sucesso!
        </SweetAlert>
      )}

      {deleteErrorAlert && (
        <SweetAlert
          error
          title="Atenção"
          onConfirm={() => setDeleteErrorAlert(false)}
        >
          Não foi possível excluir a pontuação.
        </SweetAlert>
      )}

      <WaitExport
        isOpen={exportInProgress}
        name="Ranking"
        preposicao="do"
        finishExport={finishExport}
        setFinishExport={setFinishExport}
        setExportInProgress={setExportInProgress}
      />

      {exportErrorAlert && (
        <SweetAlert
          error
          title="Atenção"
          onConfirm={() => setExportErrorAlert(false)}
        >
          Não foi possível exportar o ranking.
        </SweetAlert>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 10,
        }}
      >
        {countData > 0 && (
          <Button
            color="success"
            className="btn-rounded waves-effect waves-light events-button-add"
            style={{ width: 160 }}
            onClick={() => exportRanking()}
          >
            Exportar Ranking
          </Button>
        )}
      </div>

      <Row style={{ marginBottom: 10 }}>
        <Col md={4} style={{ marginTop: 10 }}>
          <label className="form-label text-sm">Configuração do Ranking</label>
          <select
            className="form-control"
            value={rankingCode}
            onChange={(e) => {
              setRankingCode(e.target.value);
              setCurrentIndex(1);
            }}
          >
            {rankingConfigs.map((config) => (
              <option key={config.code} value={config.code}>
                {config.description}
              </option>
            ))}
          </select>
        </Col>

        <Col md={8} style={{ marginTop: 10 }}>
          <label className="form-label text-sm">Participante</label>
          <form
            className="app-search p-0"
            onSubmit={(e) => e.preventDefault()}
            style={{ width: "100%" }}
          >
            <div className="position-relative">
              <input
                value={search}
                className="form-control"
                placeholder="Pesquisar..."
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentIndex(1);
                }}
              />
              <span className="bx bx-search-alt" />
            </div>
          </form>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table>
          <thead className="table-light">
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ whiteSpace: "nowrap" }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <LoadingSkelentonTable lines={10} cols={columns.length} />
            ) : dataList.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  Nenhuma pontuação encontrada.
                </td>
              </tr>
            ) : (
              dataList.map((row, i) => (
                <tr
                  key={`${row.loginId}-${i}`}
                  style={{ cursor: "pointer" }}
                  onDoubleClick={() => {
                    selectPlayer(row);
                    setExtractPointsModal(true);
                  }}
                >
                  {columns.map((column) => (
                    <td key={column.key}>{renderCell(row, column.key)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Pagination
        data={dataList}
        search={search}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        countData={countData}
        maxItemPerPage={itemsPerPage}
        setMaxItemPerPage={setItemsPerPage}
        maxPageButtonsToShow={3}
      />
    </>
  );
}
